import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { color, useAuthedRequestFn, transBudgetRecommendationformData, IntlNumberParser, FormatUtils } from '@my/util'
import { XStack, YStack, FinancialTable, SimpleText, ActiveButton, ScrollView, TZASummaryCards, TZASummaryCardsContainer, PriorityBadge, useMedia } from '@my/ui'
import { FinancialReviewHeading } from '../../../components/index'
import { RecommendationCardItem } from './recommendationCard'
import RecommendationChart from './RecommendationChart'
import { QuickWinCard } from './quickWinCard'
import assets from '../../../assets/images/index'
import { ExpandableInsightsCard } from '../insights/expandableInsightCard'
import { InsightsCardProps } from '../insights/expandableInsightCard/types'
import { Platform } from 'react-native'

// Constants for Quick Wins
const QUICK_WINS = [
  {
    title: 'Quick Win 1',
    timeLeft: 2,
    themeColor: color.tzaGreen,
    status: 'In progress',
    details:
      'Lorem ipsum dolor sit amet consectetur adipisicing elit. Adipisci nemo veritatis explicabo qui nobis, inventore aliquid accusamus.',
  },
  {
    title: 'Quick Win 2',
    timeLeft: 2,
    themeColor: color.tzaGreen,
    status: 'Complete',
    details:
      'Lorem ipsum dolor sit amet consectetur adipisicing elit. Adipisci nemo veritatis explica.',
  },
  {
    title: 'Quick Win 3',
    timeLeft: 2,
    themeColor: color.tzaGreen,
    status: 'In progress',
    details: 'Lorem ipsum dolor sit amet consectetur adipisicing elit.',
  },
]

// Budget Recommendation Data
const BUDGET_RECOMMENDATION_DATA = [
  {
    category: 'Housing',
    currentAmount: '$121',
    recommendedAmount: '$200',
    change: { value: '$128', isGain: true },
  },
  {
    category: 'Food',
    currentAmount: '$200',
    recommendedAmount: '$150',
    change: { value: '$18', isGain: false },
  },
  {
    category: 'Transportation',
    currentAmount: '$121',
    recommendedAmount: '$121',
    change: { value: '$108', isGain: false },
  },
  {
    category: 'Electricity',
    currentAmount: '$20',
    recommendedAmount: '$11',
    change: { value: '$9', isGain: false },
  },
  {
    category: 'Food',
    currentAmount: '$200',
    recommendedAmount: '$150',
    change: { value: '$18', isGain: false },
  },
  {
    category: 'Transportation',
    currentAmount: '$121',
    recommendedAmount: '$121',
    change: { value: '$108', isGain: false },
  },
  {
    category: 'Electricity',
    currentAmount: '$20',
    recommendedAmount: '$11',
    change: { value: '$9', isGain: false },
  },
]

const transformCashFlowsTableRows = (rows: Array<[string, string]>, recommendations: Array<{currentPropertyKey: string}>, prefix?: string) => {
  //console.log({recommendations})
  return rows.map((row) => {
    const [rowkey, rawStr] = row;
    const val = IntlNumberParser(rawStr);
    const category = prefix ? rowkey.replace(prefix, '') : rowkey;
    const recommendedChanges = recommendations
    .filter((rec) => rec.currentPropertyKey === rowkey)
    .map((rec) => rec['change']['raw'] ? (rec['change'].isGain ? '+' : '')+rec['change']['raw'] : '')
    //.reduce((prev, curr) => prev+curr, '');
    const output = {
      category,
      currentAmount: FormatUtils.currency.cashFormat(val),
      recommendedAmount: '-',
      change: '-',
    };
    if (recommendedChanges.length) {
      const evalStr = recommendedChanges.reduce((prev, curr) => prev+curr, '');
      //console.log(row, recommendedChanges, 'eval:', evalStr)
      const cumChange = eval(evalStr);
      output['recommendedAmount'] = FormatUtils.currency.cashFormat(val + cumChange);
      output['change'] = {
        value: FormatUtils.currency.cashFormat(Math.abs(cumChange)),
        isGain: cumChange >= 0
      } as any;
    }
    //console.log({row, output})
    return output;
  })  
}

export const Recommendations = () => {
  const [showMore, setShowMore] = useState(false)
  const authedRequestFn = useAuthedRequestFn()
  const { data: financialReviewData } = useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })

  const budgetRecommendation = financialReviewData?.recommendedBudget?.budgetRecommendation || [];
  const CashFlowsTable = financialReviewData?.financialSnapshotUI?.CashFlowsTable || [];
  const monthlyCashFlows = financialReviewData?.monthlyCashFlows || {};

  const BUDGET_RECOMMENDATION_DATA2 = transBudgetRecommendationformData(budgetRecommendation);

  const BUDGET_RECOMMENDATION_DATA = [
    /* {
      category: 'INCOME',
      currentAmount: '',
      recommendedAmount: '',
      //change: {},
    }, */
    //...transformCashFlowsTableRows(Object.entries(monthlyCashFlows?.Operating?.Income?.values || {}), BUDGET_RECOMMENDATION_DATA2, 'Income - '),
    {
      category: 'EXPENSES',
      currentAmount: '',
      recommendedAmount: '',
      //change: {},
    },
    ...transformCashFlowsTableRows(Object.entries(monthlyCashFlows?.Operating?.Expenses?.values || {}), BUDGET_RECOMMENDATION_DATA2, 'Expenses - Monthly - '),
    {
      category: 'INVESTING',
      currentAmount: '',
      recommendedAmount: '',
      //change: {},
    },
    ...transformCashFlowsTableRows(Object.entries(monthlyCashFlows?.['Investing/Saving/Debt Payments']?.Investing?.values || {}), BUDGET_RECOMMENDATION_DATA2, 'Investing - Monthly - '),
    {
      category: 'SAVING',
      currentAmount: '',
      recommendedAmount: '',
      //change: {},
    },
    ...transformCashFlowsTableRows(Object.entries(monthlyCashFlows?.['Investing/Saving/Debt Payments']?.Saving?.values || {}), BUDGET_RECOMMENDATION_DATA2, 'Saving - Monthly - '),
    {
      category: 'DEBT PAYMENTS',
      currentAmount: '',
      recommendedAmount: '',
      //change: {},
    },
    ...transformCashFlowsTableRows(Object.entries(monthlyCashFlows?.['Investing/Saving/Debt Payments']?.['Debt Payments']?.values || {}), BUDGET_RECOMMENDATION_DATA2, 'Debt Payments - Monthly - '),
  ]

  const quickWins = financialReviewData?.recommendations?.quickWins?.map((quickWin) => {
    if (!quickWin) return undefined;
    const {title, details, ...rec} = quickWin;
    return {
      title,
      details,
      timeLeft: rec.minutesToCompleteEstimate,
      themeColor: color.tzaGreen,
      status: 'In progress',
    };
  }).filter((x) => x) || [];

  const output = (
    <YStack p="$4" backgroundColor={'white'} style={{ flex: 1 }} gap={30}>
      {/* Financial Review Heading */}
      <FinancialReviewHeading
        title="Recommendations"
        img={assets.recommendation}
        imageStyle={{
          //'$platform-native': { width: 21, height: 21 },
          //'$platform-web': { width: 26, height: 26 },
        }}
      />

      {/* Quick Wins Section */}
      {quickWins.length > 0 ? <QuickWinsSection quickWins={quickWins} /> : null}

      <YStack gap={10} width={'100%'} justifyContent='center' alignContent='center'>
        <XStack justifyContent="flex-start" style={{width: "100%"}}>
          <SimpleText fontSize={14} lineHeight={29} fontWeight="800" color={color.darkNavy}>
            RECOMMENDATION PRIORITY
          </SimpleText>
        </XStack>
        <XStack style={{width: "100%"}}>
          <RecommendationChart priorityData={financialReviewData?.recommendationsPriorities} />
        </XStack>
      </YStack>
      
      {/* Recommendation and Budget Tables */}
      <RecommendationAndBudgetTables
        budgetData={showMore ? BUDGET_RECOMMENDATION_DATA : BUDGET_RECOMMENDATION_DATA2}
        showMore={showMore}
        setShowMore={setShowMore}
        priorityData={financialReviewData?.recommendationsPriorities}
        fullDataLength={BUDGET_RECOMMENDATION_DATA.length}
      />
      
        {/* Specific Recommendations Section */}
        {financialReviewData?.recommendations?.specificRecommendations && (
          <SpecificRecommendations recommendations={financialReviewData?.recommendations?.specificRecommendations} />
        )}
      
    </YStack>
  )

  return (
      Platform.OS === 'web' ? (
          output
        ) : (
          <ScrollView backgroundColor={color.white}>
            {output}
          </ScrollView>
        )
    )
}

// Quick Wins Section Component
const QuickWinsSection: React.FC<{quickWins: typeof QUICK_WINS}> = ({quickWins}) => (
  <YStack>
    <XStack justifyContent="flex-start" width="100%" marginBottom={20}>
      <SimpleText color={color.darkNavy} style={{ fontSize: 14, lineHeight: 19, fontWeight: '800' }}>
        QUICK WINS
      </SimpleText>
    </XStack>
    <TZASummaryCardsContainer
      children={quickWins.slice(0,3).map((win, index) => (
        <QuickWinCard
          key={index}
          title={win.title}
          timeLeft={win.timeLeft}
          themeColor={win.themeColor}
          details={win.details}
          status={win.status}
        />
      ))}
    />
    {/* <YStack flexDirection="row" flexWrap="wrap" justifyContent="space-between" gap={10}>
      {quickWins.slice(0,3).map((win, index) => (
        <QuickWinCard
          key={index}
          title={win.title}
          timeLeft={win.timeLeft}
          themeColor={win.themeColor}
          details={win.details}
          status={win.status}
        />
      ))}
    </YStack> */}
  </YStack>
)

// Recommendation and Budget Tables Component
const RecommendationAndBudgetTables = ({
  budgetData,
  showMore,
  setShowMore,
  priorityData,
  fullDataLength
}: any) => {
  const media = useMedia();
  return <YStack flexDirection={'row'} flexWrap="wrap">
    {/* <YStack>
      <SimpleText fontSize={14} lineHeight={29} fontWeight="800" color={color.grey}>
        RECOMMENDATION PRIORITY
      </SimpleText>
      <RecommendationChart priorityData={priorityData} />
    </YStack> */}

    <YStack gap={10} width={'100%'} justifyContent='center' alignContent='center'>
      <XStack justifyContent="flex-start" width="100%">
        <SimpleText
          fontSize={14}
          lineHeight={29}
          marginBottom={10}
          fontWeight="800"
          color={color.darkNavy}
        >
          BUDGET RECOMMENDATION
        </SimpleText>
      </XStack>
      <YStack maxWidth={920} gap={5}>
        <ScrollView
          //maxHeight={500}
          maxWidth={920}
          //minWidth={320}
          contentContainerStyle={{ flexDirection: 'row', width: '100%', zi: 10 }}
        >
          <FinancialTable
            data={showMore ? budgetData : budgetData.slice(0, 4)}
            headings={[
              { key: 'category', text: 'Category' },
              { key: 'currentAmount', text: 'Current' },
              { key: 'recommendedAmount', text: 'Recommended' },
              { key: 'change', text: 'Change' },
            ]}
            //columnWidth={120}
            tableContainerStyle={{maxHeight: 500, overflow: 'auto'}}
            mobileRenderOptions={{
              titleKey: 'category',
              mainValueKeys: ['change'],
              //interactiveKeys: ['actionButtons'],
              otherKeys: [['currentAmount'],['recommendedAmount']]
            }}
          />
        </ScrollView>

        {fullDataLength > 1 && !media['max-sm'] ? (
          <XStack justifyContent={'flex-end'} alignItems={'center'}>
            <ActiveButton 
              style={{width: 120, height: 30, padding: 0}} 
              onPress={() => setShowMore(!showMore)} 
              variant={showMore ? 'outlined' : undefined }
              ButtonTextProps={{
                children: showMore ? 'Show Less...' : 'Show More...'
              }}
            />
          </XStack>
        ) : null}
      </YStack>
    </YStack>
  </YStack>
}

// Specific Recommendations Section Component
const SpecificRecommendations = ({ recommendations }: any) => {
  const media = useMedia();
  const maxWidth = media['max-sm'] ? 380 : 380
  return <YStack gap={20} mt="$4" width={'100%'} justifyContent='center' alignContent='center'>
    <XStack justifyContent="flex-start" width="100%">
      <SimpleText color={color.darkNavy} style={{ fontSize: 14, lineHeight: 19, fontWeight: '800' }}>
        SPECIFIC RECOMMENDATIONS
      </SimpleText>
    </XStack>
      <XStack flexWrap="wrap" width={'100%'} maxWidth={1100} gap={20}>
        {recommendations?.map((recommendation: any, index: number) => (
          <XStack key={index} $platform-native={{ maxWidth }} /* maxWidth={380} minWidth={350} */ style={{width: '100%', maxWidth}}>
            <ExpandableInsightsCard 
              key={index}
              maxWidth={maxWidth}
              {...({
                title: recommendation.title,
                value: (<XStack gap={10} alignItems='center'>
                  <PriorityBadge priority={recommendation.urgency} />
                  <SimpleText
                    style={Platform.select({native: {maxWidth: 200}, web: {}})}
                  >Strategy: {recommendation.strategy}</SimpleText>
                </XStack>),
                headerImage: assets.recommendation,
                //extraHeaderComponent: 
              } as InsightsCardProps)}
            >
              <RecommendationCardItem
                //key={index}
                title={recommendation.title}
                priority={recommendation.urgency}
                immediateAction={recommendation.immediateAction}
                monthlyImpact={recommendation.monthlyImpact}
                longTermImpact={recommendation.longTermImpact}
                goals={recommendation.goals}
                effort={recommendation.effort}
                onImplement={() => alert(`Implementing action: ${recommendation.title}`)}
                strategy={recommendation.strategy}
                justBody
              />
            </ExpandableInsightsCard>
          </XStack>
        ))}
      </XStack>
  </YStack>
}
